<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-1 text-center" v-text="configTariffTitle" />
            <h5 class="mb-0 text-center" v-text="'Twój pakiet'" />
        </template>

        <div
            v-text="`Pokazane kwoty są stawkami bazowymi dla jednego sklepu.
                Ostateczna kwota zależy od konfiguracji sklepów i urządzeń.`"
            class="text-sm a-infoBar a-infoBar--warning mb-3"
        />

        <page-preloader v-if="isLoader" min-height="345px" />

        <template v-else-if="tariffGroups.length">
            <div
                v-if="isYearlyTariffBlocked"
                class="d-flex flex-column flex-md-row gap-1 my-3 align-items-start align-items-md-center"
            >
                <div
                    v-text="$options.yearlyTariffBlockedDesc"
                    class="a-infoBar a-infoBar--warning text-sm flex-fill w-100"
                />
                <the-btn
                    text="Przejdź do płatności"
                    class="btn btn-secondary"
                    @click="goToPayments"
                />
            </div>
            <tariff-group
                v-for="group of tariffGroupsWithAvailability"
                :key="group.id"
                :group="group"
                class="mb-3"
            />
        </template>

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Zamknij"
                @click="$emit('close-modal')"
            />
            <the-btn
                class="btn-secondary"
                text="Przejdź do konfiguracji pakietu"
                :disabled="isLoader || !canContinue"
                @click="saveConfiguration"
            />
        </template>
    </ui-modal>
</template>

<script>
import { SERVICE_CODE } from '@/store/modules/drs/drs.module'
import { mapActions, mapGetters } from 'vuex'
import { CONFIG_MODES } from '@/store/modules/drs/tariffs.module'
import { TIMELINES } from '@/utils/constants/drs/tariffs'
import TariffGroup from '@/components/drs/configuration/TariffGroup.vue'
import cloneDumb from '@/helpers/cloneDumb'

export default {
    yearlyTariffBlockedDesc: 'Jeśli chcesz wybrać pakiet roczny, opłać aktualny dokument rozliczeniowy',

    data: () => ({
        isLoader: false
    }),

    async created() {
        if (this.args?.createCopy && this.selectedTariff) {
            this.$store.commit('drsTariffs/SET_TARIFFS_COPY', cloneDumb(this.selectedTariff))
        }

        const hadPreviousConfig = await this.loadPreviousConfiguration()
        await this.fetchData()

        if (hadPreviousConfig && this.args?.loadFromStore) {
            this.$store.dispatch('openModal', {
                componentPath: 'drs/configuration/ConfigurationSummary'
            })
        }
    },

    computed: {
        ...mapGetters('drsTariffs', ['tariffs', 'selectedTariff', 'tariffGroups', 'configTariffTitle', 'configMode']),
        ...mapGetters('payment', ['hasUnpaidDocuments']),

        args () {
            return this.$store.getters.modalState.args
        },

        loadTariffsParams() {
            return this.args?.loadTariffsParams || {}
        },

        drsService() {
            return this.$store.getters.getServiceByCode(SERVICE_CODE)
        },

        isYearlyTariffBlocked() {
            return this.configMode === CONFIG_MODES.EDIT && this.hasUnpaidDocuments
        },

        tariffGroupsWithAvailability() {
            return this.tariffGroups.map(group => ({
                ...group,
                tariffs: group.tariffs.map(tariff => {
                    // Mark yearly tariffs as unavailable when editing a package with an unpaid document
                    const shouldBlockTariff = this.isYearlyTariffBlocked && tariff.timeline === TIMELINES.YEARLY

                    if (!shouldBlockTariff) {
                        return tariff
                    }

                    return {
                        ...tariff,
                        available: false,
                        availability_description: tariff.available === false
                            ? tariff.availability_description
                            : this.$options.yearlyTariffBlockedDesc
                    }
                })
            }))
        },

        canContinue() {
            const selectedTariff = this.tariffGroupsWithAvailability
                .flatMap(group => group.tariffs)
                .find(tariff => tariff.id === this.selectedTariff?.id)

            // Prevent continuing with an unavailable tariff
            return !!selectedTariff && selectedTariff.available !== false
        }
    },

    methods: {
        ...mapActions('drs', ['fetchLocationsWithCompanyId', 'loadSubscriptions']),

        ...mapActions('drsTariffs', ['loadDrsTariffs', 'loadDevices', 'loadTariffConfigFromStorage']),

        async fetchData() {
            if (this.args?.noFetch) {
                return
            }

            this.isLoader = true
            await this.loadDrsTariffs(this.loadTariffsParams)
            await this.fetchLocationsWithCompanyId()
            await this.loadSubscriptions()
            this.isLoader = false
        },

        async goToPayments() {
            await this.$store.dispatch('toggleModal', false)
            this.$router.push({
                name: 'drs-payment',
                params: { returnUrl: 'drs-stores-licenses' }
            })
        },

        async saveConfiguration() {
            this.$store.dispatch('openModal', {
                componentPath: 'drs/configuration/SelectShop'
            })
        },

        async loadPreviousConfiguration() {
            // do not restore tariff configuration on the Licenses tab
            if (this.$router.currentRoute.name === 'drs-stores-licenses') return false

            const tariffConfig = await this.$store.$storage.getItem('tariffConfig')
            if (tariffConfig) {
                await this.loadTariffConfigFromStorage()
                return true
            }

            return false
        }
    },

    components: {
        TariffGroup
    }
}
</script>